import { v4 as uuid } from 'uuid'

import { UseCase } from '@/domain/core/UseCase'
import { Result } from '@/domain/protocols/Result'
import {
  ALLOWED_USER_CHART_EXTENSIONS,
  ConvertedChartData,
  MAX_USER_CHARTS,
  USER_CHART_MAX_SIZE_MB_MBTILES,
  USER_CHART_MAX_SIZE_MB_PDF_TIFF,
  UserChartExtension
} from '@/domain/protocols/UserChart'

import type { IUserChartRepository } from '@/data/UserChartRepository/IUserChartRepository'

export interface PrepareUserChartRequest {
  file: File
  onProgress?: (percent: number, speedBps: number) => void
  signal?: AbortSignal
  // When replacing the file of an existing chart (recovering an old chart whose files
  // are gone on the server), reuse its id instead of minting a new one and skip the
  // per-account cap (no new chart is created).
  chartId?: string
}

export interface PrepareUserChartResponse extends ConvertedChartData {
  baseName: string
}

export class PrepareUserChartUseCase implements UseCase<
  PrepareUserChartRequest,
  Promise<Result<PrepareUserChartResponse>>
> {
  constructor(private readonly repository: IUserChartRepository) {}

  public static getExtension(fileName: string): string {
    const dotIndex = fileName.lastIndexOf('.')
    if (dotIndex < 0) return ''
    return fileName.slice(dotIndex + 1).toLowerCase()
  }

  public static getBaseName(fileName: string): string {
    const dotIndex = fileName.lastIndexOf('.')
    if (dotIndex < 0) return fileName
    return fileName.slice(0, dotIndex)
  }

  public static validate(file: File): Result<void> {
    const ext = PrepareUserChartUseCase.getExtension(file.name) as UserChartExtension

    if (!ALLOWED_USER_CHART_EXTENSIONS.includes(ext)) {
      return Result.fail('INVALID_FORMAT')
    }

    const maxMb = ext === 'mbtiles' ? USER_CHART_MAX_SIZE_MB_MBTILES : USER_CHART_MAX_SIZE_MB_PDF_TIFF
    const maxBytes = maxMb * 1024 * 1024

    if (file.size > maxBytes) {
      return Result.fail('FILE_TOO_LARGE')
    }

    return Result.ok()
  }

  async execute(request: PrepareUserChartRequest): Promise<Result<PrepareUserChartResponse>> {
    const { file, onProgress, signal } = request
    const isReplacement = !!request.chartId

    const validation = PrepareUserChartUseCase.validate(file)
    if (validation.isFailure) {
      return Result.fail(validation.error)
    }

    // Block when the per-account cap is reached; a failed read is tolerated so a
    // transient storage error doesn't prevent importing. A replacement re-uses an
    // existing chart, so it does not count against the cap.
    if (!isReplacement) {
      const chartsResult = await this.repository.getCharts()
      if (chartsResult.isSuccess && chartsResult.getValue().length >= MAX_USER_CHARTS) {
        return Result.fail('USER_CHARTS_LIMIT_REACHED')
      }
    }

    const chartId = request.chartId ?? uuid()

    // The converter web client reports transport progress as (bytesLoaded, bytesTotal);
    // bridge it to the (percent, speedBps) shape the upload screen's speed ring expects.
    let lastLoaded = 0
    let lastTimestamp = Date.now()
    const handleProgress = onProgress
      ? (loaded: number, total?: number) => {
          const now = Date.now()
          const elapsedSeconds = (now - lastTimestamp) / 1000
          const speedBps = elapsedSeconds > 0 ? (loaded - lastLoaded) / elapsedSeconds : 0
          lastLoaded = loaded
          lastTimestamp = now
          const percent = total && total > 0 ? (loaded / total) * 100 : 0
          onProgress(percent, speedBps)
        }
      : undefined

    const result = await this.repository.convertChart({
      file,
      chartId,
      onProgress: handleProgress,
      signal
    })

    if (result.isFailure) {
      return Result.fail(result.error)
    }

    return Result.ok({
      ...result.getValue(),
      baseName: PrepareUserChartUseCase.getBaseName(file.name)
    })
  }
}
